import { useState, useRef } from 'react'
import { strapiApi } from '../../services/api'
import Form from "../Form/Form"
import Button from '../Button/Button'
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext'
import style from './feedbackform.module.scss'

const FeedbackForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        subject: '',
        text: ''
    })
    const [formErrors, setFormErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [lastSubmitTime, setLastSubmitTime] = useState(0)
    const { settings } = useAccessibility()

    // Ref для таймера
    const submitTimerRef = useRef(null)

    // Accessibility-aware colors
    const containerBg = settings.contrast === 'default' ? '#fff' : 'var(--bg-primary)';
    const textColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--text-primary)';
    const borderColor = settings.contrast === 'default' ? '#ddd' : 'var(--text-primary)';
    const errorColor = settings.contrast === 'default' ? '#e53e3e' : '#ff6b6b';
    const inputBg = settings.contrast === 'default' ? '#fff' : 'var(--bg-secondary)';

    // Валидация формы
    const validateForm = () => {
        const errors = {};

        // Валидация имени
        if (!formData.name.trim()) {
            errors.name = 'Имя обязательно для заполнения';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Имя должно содержать минимум 2 символа';
        }

        // Валидация телефона
        if (!formData.phone.trim()) {
            errors.phone = 'Телефон обязателен для заполнения';
        } else {
            const phoneRegex = /^[\+]?[78]?[-\s]?\(?\d{3}\)?[-\s]?\d{3}[-\s]?\d{2}[-\s]?\d{2}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
                errors.phone = 'Введите корректный номер телефона';
            }
        }

        // Валидация email
        if (!formData.email.trim()) {
            errors.email = 'Email обязателен для заполнения';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Введите корректный email адрес';
        }

        // Валидация темы
        if (!formData.subject.trim()) {
            errors.subject = 'Тема обязательна для заполнения';
        } else if (formData.subject.trim().length < 5) {
            errors.subject = 'Тема должна содержать минимум 5 символов';
        }

        // Валидация сообщения
        if (!formData.text.trim()) {
            errors.text = 'Сообщение обязательно для заполнения';
        } else if (formData.text.trim().length < 10) {
            errors.text = 'Сообщение должно содержать минимум 10 символов';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Очистка ошибки при изменении поля
    const clearError = (fieldName) => {
        setFormErrors(prev => ({
            ...prev,
            [fieldName]: ''
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Очищаем ошибку при изменении поля
        if (formErrors[field]) {
            clearError(field);
        }
    }

    // Форматирование телефона
    const formatPhone = (phone) => {
        return phone.replace(/\D/g, '');
    };

    const handleSubmit = async (formData) => {
        // Проверяем время последней отправки (минимум 5 секунд между запросами)
        const now = Date.now();
        const timeSinceLastSubmit = now - lastSubmitTime;
        
        if (timeSinceLastSubmit < 5000) {
            alert('Пожалуйста, подождите 5 секунд перед повторной отправкой формы');
            return;
        }

        // Валидация формы
        if (!validateForm()) {
            alert('Пожалуйста, исправьте ошибки в форме');
            return;
        }

        setIsSubmitting(true)
        setLastSubmitTime(now)
        
        try {
            // Исправленный формат данных согласно бэкенду
            const requestData = {
                data: {
                    attributes: {
                        Name: formData.name.trim(),
                        Phone: formatPhone(formData.phone),
                        Email: formData.email.trim(),
                        Subject: formData.subject.trim(),
                        Text: formData.text.trim(),
                    }
                }
            }

            console.log('Sending feedback data:', requestData)

            await strapiApi.post('/api/feedbacks', requestData)
            setIsSubmitted(true)
            setFormData({
                name: '',
                phone: '',
                email: '',
                subject: '',
                text: ''
            })
        } catch (error) {
            console.error('Error submitting feedback:', error)
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               'Произошла ошибка при отправке формы. Пожалуйста, попробуйте снова.'
            alert(errorMessage)
            
            // Сбрасываем таймер при ошибке
            setLastSubmitTime(0)
        } finally {
            setIsSubmitting(false)
        }
    }

    // Проверяем, можно ли отправить форму
    const canSubmit = !isSubmitting && (Date.now() - lastSubmitTime) >= 5000;

    // Компонент для отображения ошибок
    const ErrorMessage = ({ error }) => {
        if (!error) return null;
        
        return (
            <div 
                className={style.errorMessage}
                style={{ color: errorColor }}
                role="alert"
                aria-live="polite"
            >
                {error}
            </div>
        );
    };

    const inputs = {
        name: {
            name: 'Имя',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Ваше имя*',
            background: inputBg,
            color: textColor,
            border: formErrors.name ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '4px',
            padding: '0 15px',
            value: formData.name,
            setValue: (value) => handleInputChange('name', value),
            required: true,
        },
        phone: {
            name: 'Телефон',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Ваш телефон* (например: 8-999-123-45-67)',
            background: inputBg,
            color: textColor,
            border: formErrors.phone ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '4px',
            padding: '0 15px',
            value: formData.phone,
            setValue: (value) => handleInputChange('phone', value),
            required: true,
        },
        email: {
            name: 'Email',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Ваш email*',
            background: inputBg,
            color: textColor,
            border: formErrors.email ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '4px',
            padding: '0 15px',
            value: formData.email,
            setValue: (value) => handleInputChange('email', value),
            required: true,
        },
        subject: {
            name: 'Тема',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Тема обращения*',
            background: inputBg,
            color: textColor,
            border: formErrors.subject ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '4px',
            padding: '0 15px',
            value: formData.subject,
            setValue: (value) => handleInputChange('subject', value),
            required: true,
        },
        text: {
            name: 'Сообщение',
            type: 'textarea',
            width: '100%',
            height: '150px',
            margin: '0 0 5px 0',
            placeholder: 'Ваше сообщение* (минимум 10 символов)',
            background: inputBg,
            color: textColor,
            border: formErrors.text ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '4px',
            padding: '15px',
            value: formData.text,
            setValue: (value) => handleInputChange('text', value),
            required: true,
        },
    }

    if (isSubmitted) {
        return (
            <div className={style.successMessage}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="#4CAF50">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <h3 style={{ color: textColor }}>Спасибо за ваше обращение!</h3>
                <p style={{ color: textColor }}>Мы свяжемся с вами в ближайшее время.</p>
            </div>
        )
    }

    return (
        <div className={style.formContainer} style={{ background: containerBg }}>
            <h2 style={{ color: textColor }} className={style.formTitle}>
                Обратная связь
            </h2>
            <p style={{ color: textColor }} className={style.formSubtitle}>
                Заполните форму и мы ответим вам в ближайшее время
            </p>
            
            <Form
                className={style.feedbackform}
                inputs={inputs}
                onSubmit={handleSubmit}
                button={
                    <div className={style.buttonContainer}>
                        <button
                            type="submit"
                            className={style.customButton}
                            disabled={!canSubmit}
                        >
                            {isSubmitting ? 'Отправка...' : 'Отправить сообщение'}
                        </button>
                        {!canSubmit && (Date.now() - lastSubmitTime) < 5000 && (
                            <div 
                                className={style.cooldownMessage}
                                style={{ color: textColor }}
                            >
                                Следующее сообщение можно отправить через {Math.ceil((5000 - (Date.now() - lastSubmitTime)) / 1000)} сек.
                            </div>
                        )}
                    </div>
                }
            />
            
            {/* Отображение ошибок */}
            <div className={style.errorsContainer}>
                <ErrorMessage error={formErrors.name} />
                <ErrorMessage error={formErrors.phone} />
                <ErrorMessage error={formErrors.email} />
                <ErrorMessage error={formErrors.subject} />
                <ErrorMessage error={formErrors.text} />
            </div>
        </div>
    )
}

export default FeedbackForm